// HUNGER and the HOSPITAL — the rules, as pure functions (no state, no timers) so they can be tested in Node:
//   • hungerStage(elapsedMs, cfg)      the stage the citizens are in, `elapsedMs` after the last FEED
//   • hospitalHealCost(n, feed, cfg)   what it costs to heal n patients in the hospital (×healCostMult the FEED rate — neglect is dear)
//   • patientDue(at, now, cfg)         true when a patient admitted at `at` has been left too long (dies)
//   • patientTimeLeftMs(at, now, cfg)  how long that patient has left
//   • gravesAfter(graves, reason)      the kingdom's grave counts after a loss for `reason` (what the loss log records)
// Stages, in order: healthy → hungry (thin) → caution (yellow ⚠) → critical (red cross) → hospital (admissions begin).
// Numbers live in balance.js (HungerConfig, HospitalConfig).

export const HUNGER_STAGES = Object.freeze(['healthy', 'hungry', 'caution', 'critical', 'hospital'])

export function hungerStage(elapsedMs, cfg) {
  if (elapsedMs >= cfg.hospitalAfterMs) return 'hospital'
  if (elapsedMs >= cfg.dyingAfterMs) return 'critical'
  if (elapsedMs >= cfg.cautionAfterMs) return 'caution'
  if (elapsedMs >= cfg.skinnyAfterMs) return 'hungry'
  return 'healthy'
}

// The old three-way reading some code still uses: 'healthy' | 'skinny' | 'dying'.
export function legacyHungerState(stage) {
  if (stage === 'healthy') return 'healthy'
  if (stage === 'hungry' || stage === 'caution') return 'skinny'
  return 'dying'
}

export const stageAtLeast = (stage, floor) => HUNGER_STAGES.indexOf(stage) >= HUNGER_STAGES.indexOf(floor)

export function hospitalHealCost(patients, feedCostPerCitizen, cfg) {
  return Math.max(0, patients) * feedCostPerCitizen * cfg.healCostMult
}

export const patientDue = (admittedAt, now, cfg) => now - admittedAt >= cfg.stayMs

// A patient's CONDITION (a cfg.conditions entry: mild → serious → severe → terminal) by how far through the stay it is, and what
// treating it costs now. A terminal patient cannot be saved: its treatment is charged in full and it dies.
export function patientCondition(admittedAt, now, cfg) {
  const f = Math.max(0, (now - admittedAt) / cfg.stayMs)
  return cfg.conditions.find((c) => f < c.untilFrac) ?? cfg.conditions[cfg.conditions.length - 1]
}
// HOME CARE: what a service costs when you do not own the building for it (mult = HomeCare.schoolMult / healthMult), the base rate when you do.
export const homeCareMult = (ownsBuilding, mult) => (ownsBuilding ? 1 : mult)
export const homeCareCost = (base, ownsBuilding, mult) => Math.round(base * homeCareMult(ownsBuilding, mult))
export const patientHealCost = (admittedAt, now, feedCostPerCitizen, cfg, mult = 1) => Math.round(feedCostPerCitizen * cfg.healCostMult * patientCondition(admittedAt, now, cfg).costMult * mult)
// The price of treating every patient who CAN be saved (everyone but the terminal ones).
export const treatableCost = (admits, now, feedCostPerCitizen, cfg, mult = 1) => admits.reduce((sum, at) => (patientCondition(at, now, cfg).fatal ? sum : sum + patientHealCost(at, now, feedCostPerCitizen, cfg, mult)), 0)
export const patientTimeLeftMs = (admittedAt, now, cfg) => Math.max(0, cfg.stayMs - (now - admittedAt))

// A loss reason (as written to profile.citizenLoss) → which grave it is.
export function graveKindOf(reason = '') {
  const r = String(reason).toLowerCase()
  if (r.includes('lineage')) return null // a citizen who became someone else is not a loss
  if (r.includes('starv')) return 'starved'
  if (r.includes('feral')) return 'feral'
  if (r.includes('hospital')) return 'hospital'
  if (r.includes('shot')) return 'shot'
  return 'other'
}

export function gravesAfter(graves, reason, n = 1) {
  const kind = graveKindOf(reason)
  if (!kind) return graves ?? {}
  const g = { ...(graves ?? {}) }
  g[kind] = (g[kind] ?? 0) + n
  g.total = (g.total ?? 0) + n
  return g
}

export const gravesTotal = (graves) => Number(graves?.total ?? 0) || 0
